import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { X, Upload, Image as ImageIcon, File as FileIcon } from 'lucide-react'
import { uploadFileService } from '@/services/upload_file_service'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import { DeleteAlert } from '@/components/DeleteAlert'
import { logger } from '@magnetar/utils'

interface UploaderProps {
  folder?: string
  onUploadSuccess?: (result: { uuid: string; meta?: Record<string, unknown> }) => void
  onUploadError?: (error: Error) => void
  onDelete?: () => void
  className?: string
  /**
   * 允許的文件類型，例如 ['.mp4'] 或 ['.jpeg', '.webp']
   * 如果不提供，則允許所有文件類型
   */
  allowedFileTypes?: string[]
  /**
   * 檔案大小上限（bytes），例如 100 * 1024 * 1024 表示 100MB
   * 如果不提供，則不限制大小
   */
  maxFileSize?: number
}

interface UploadedFile {
  file: File
  preview: string
  uuid?: string
  uploading: boolean
  signedUrl?: string
}

export function Uploader({
  folder = '',
  onUploadSuccess,
  onUploadError,
  onDelete,
  className,
  allowedFileTypes,
  maxFileSize,
}: UploaderProps) {
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null)
  const [showDeleteAlert, setShowDeleteAlert] = useState(false)
  const { token } = useAuth()

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0]
      if (!file) return

      // 創建預覽URL
      const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : ''

      setUploadedFile({
        file,
        preview,
        uploading: true,
      })

      try {
        const result = await uploadFileService.uploadFile(file, folder, token)

        if (result.success) {
          // 取得 Signed URL 用於預覽
          let signedUrl: string | undefined
          try {
            signedUrl = await uploadFileService.getFileDownloadUrl(result.data.uuid, token)
          } catch (error) {
            logger.error('取得預覽 URL 失敗:', error)
            // 預覽 URL 失敗不影響上傳成功
          }

          setUploadedFile((prev) =>
            prev
              ? { ...prev, uuid: result.data.uuid, uploading: false, signedUrl }
              : null,
          )
          onUploadSuccess?.(result.data)
          toast.success('檔案已成功上傳')
        } else {
          throw new Error(result.error?.message || '上傳失敗')
        }
      } catch (error) {
        setUploadedFile(null)
        const err = error instanceof Error ? error : new Error('上傳失敗')
        onUploadError?.(err)
        toast.error(err.message)
      }
    },
    [folder, token, onUploadSuccess, onUploadError],
  )

  const handleDeleteConfirm = async () => {
    if (uploadedFile?.uuid) {
      try {
        const result = await uploadFileService.deleteFile(uploadedFile.uuid, token)
        if (result.success) {
          setUploadedFile(null)
          onDelete?.()
          toast.success('檔案已成功刪除')
        } else {
          throw new Error(result.error?.message || '刪除失敗')
        }
      } catch (error) {
        const err = error instanceof Error ? error : new Error('刪除失敗')
        toast.error(err.message)
      } finally {
        setShowDeleteAlert(false)
      }
    }
  }

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    multiple: false,
    maxSize: maxFileSize,
    accept: allowedFileTypes?.reduce(
      (acc, type) => {
        // 根據副檔名決定 MIME 類型
        switch (type.toLowerCase()) {
          case '.mp4':
            acc['video/mp4'] = ['.mp4']
            break
          case '.apk':
            acc['application/vnd.android.package-archive'] = ['.apk']
            break
          case '.jpeg':
          case '.jpg':
            acc['image/jpeg'] = ['.jpeg', '.jpg']
            break
          case '.webp':
            acc['image/webp'] = ['.webp']
            break
          case '.png':
            acc['image/png'] = ['.png']
            break
          default:
            // 其他文件類型可以繼續添加
            break
        }
        return acc
      },
      {} as Record<string, string[]>,
    ),
  })

  // 清理預覽URL
  // const cleanupPreview = () => {
  //   if (uploadedFile?.preview) {
  //     URL.revokeObjectURL(uploadedFile.preview)
  //   }
  // }

  return (
    <div className={className}>
      {!uploadedFile ? (
        <div
          {...getRootProps()}
          className={cn(
            'border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors',
            'hover:border-primary/50 hover:bg-muted/50 w-full h-full',
            isDragActive && 'border-primary bg-muted',
          )}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <Upload className="w-8 h-8" />
            <p>拖放檔案到此處，或點擊選擇檔案</p>
            {allowedFileTypes && (
              <p className="text-sm text-gray-500">支援的檔案類型：{allowedFileTypes.join(', ')}</p>
            )}
            {maxFileSize && (
              <p className="text-sm text-gray-500">
                檔案大小上限：{Math.round(maxFileSize / 1024 / 1024)}MB
              </p>
            )}
            {fileRejections.length > 0 && (
              <p className="text-sm text-red-500">
                {fileRejections[0].errors.some((e) => e.code === 'file-too-large')
                  ? `檔案大小超過上限 ${Math.round((maxFileSize || 0) / 1024 / 1024)}MB`
                  : `不支援的檔案類型。允許的類型：${allowedFileTypes?.join(', ')}`}
              </p>
            )}
          </div>
        </div>
      ) : (
        <Card className="relative p-4">
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 text-muted-foreground hover:text-destructive"
            onClick={() => setShowDeleteAlert(true)}
            disabled={uploadedFile.uploading}
          >
            <X className="w-4 h-4" />
          </Button>

          <div className="flex items-center gap-4">
            {uploadedFile.uuid ? (
              (() => {
                // 優先使用 Signed URL，fallback 到舊的 API URL
                const fileUrl = uploadedFile.signedUrl || uploadFileService.getFileUrl(uploadedFile.uuid)
                const fileExt = uploadedFile.file.name.split('.').pop()?.toLowerCase()
                const isImageFile = ['webp', 'png', 'jpeg', 'jpg'].includes(fileExt || '')

                return isImageFile ? (
                  <img
                    src={fileUrl}
                    alt={uploadedFile.file.name}
                    className="w-16 h-16 object-cover rounded"
                  />
                ) : (
                  <div className="w-16 h-16 flex items-center justify-center bg-muted rounded">
                    <FileIcon className="w-8 h-8 text-muted-foreground" />
                  </div>
                )
              })()
            ) : (
              <div className="w-16 h-16 flex items-center justify-center bg-muted rounded">
                {uploadedFile.file.type.startsWith('image/') ? (
                  <ImageIcon className="w-8 h-8 text-muted-foreground" />
                ) : (
                  <FileIcon className="w-8 h-8 text-muted-foreground" />
                )}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{uploadedFile.file.name}</p>
              <p className="text-sm text-muted-foreground">
                {uploadedFile.uploading ? '上傳中...' : '上傳完成'}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* 刪除確認對話框 */}
      <DeleteAlert
        open={showDeleteAlert}
        onOpenChange={setShowDeleteAlert}
        onConfirm={handleDeleteConfirm}
        title="確定要刪除此檔案嗎？"
        description="此操作無法復原。這將永久刪除此檔案。"
      />
    </div>
  )
}
